import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import {
	routingRulesResource,
	type RoutingRulesResponse,
} from '@atlassian/eoc-routing-rules/resources';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { type EscalationEntity } from '../../common/types';
import { useTeamEscalationsService } from '../../services/team-escalations';
import { useEscalationsCommonController } from '../escalations-common';

export const useTeamEscalationsController = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data } = useResource(routingRulesResource);

	const { saveView, completeTask, ...service } = useTeamEscalationsService();

	const escalationsCommonController = useEscalationsCommonController(service);

	const toggleSection = () => {
		assert(
			!!data,
			new AssertionError(
				'useTeamEscalationsController-toggleSection',
				'data should not be undefined',
			),
		);

		const collapsed = data.escalations.collapsed;

		const newData: RoutingRulesResponse = {
			...data,
			escalations: {
				...data.escalations,
				collapsed: !collapsed,
			},
		};

		saveView(newData)
			.then(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicy collapsed', {
					collapsed: !collapsed,
				});
			})
			.catch(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicy collapseFailed', {
					collapsed: !collapsed,
				});
			});
	};

	const handleCollapseStatusChange = (entity: EscalationEntity) => {
		assert(
			!!data,
			new AssertionError(
				'useTeamEscalationsController-handleCollapseStatusChange',
				'data should not be undefined',
			),
		);

		const { collapsed, id } = entity;

		const newData: RoutingRulesResponse = {
			...data,
			escalations: {
				...data.escalations,
				entities: data.escalations.entities.map((entity) => {
					return entity.id === id
						? {
								...entity,
								collapsed: !collapsed,
							}
						: entity;
				}),
			},
		};

		saveView(newData)
			.then(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicy collapsed', id, {
					collapsed: !collapsed,
				});
			})
			.catch(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicy collapseFailed', id);
			});
	};

	const handleOrderChange = (
		updatedValue: EscalationEntity[],
		targetIndex: number,
		sourceId: string,
	) => {
		assert(
			!!data,
			new AssertionError(
				'useTeamEscalationsController-handleOrderChange',
				'data should not be undefined',
			),
		);

		const newData: RoutingRulesResponse = {
			...data,
			escalations: {
				...data.escalations,
				entities: updatedValue,
			},
		};

		saveView(newData)
			.then(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicyOrder changed', sourceId, {
					order: targetIndex,
				});
			})
			.catch(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'escalationPolicyOrder failed', sourceId, {
					order: targetIndex,
				});
			});
	};

	const handleEditClick = (data: EscalationEntity) => {
		escalationsCommonController.handleEditClick(data);
		completeTask();
	};

	return {
		...escalationsCommonController,
		handleEditClick,
		handleCollapseStatusChange,
		handleOrderChange,
		toggleSection,
	};
};
